import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useDebounce } from '../../hooks/useDebounce';
import type { SearchSuggestion } from '../../favorites-app/types';

interface UseMultiTickerSearchProps {
  selected: SearchSuggestion[];
  isActive: boolean;
  onSelect: (stock: SearchSuggestion) => void;
}

export const useMultiTickerSearch = ({
  selected,
  isActive,
  onSelect,
}: UseMultiTickerSearchProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<SearchSuggestion[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const suggestionsListRef = useRef<HTMLUListElement>(null);
  const searchValueRef = useRef(searchValue);
  searchValueRef.current = searchValue;

  const debouncedSearchValue = useDebounce(searchValue, 300);

  const selectedSymbols = useCallback(
    () => new Set(selected.map((s) => s.ticker_symbol.toLowerCase())),
    [selected],
  );

  useEffect(() => {
    const abortController = new AbortController();

    const performSearch = async () => {
      const term = debouncedSearchValue.trim();
      if (!term || !searchValueRef.current.trim()) {
        setSearchSuggestions([]);
        return;
      }

      try {
        const response = await axios.get(
          `/api/tickers?search_term=${encodeURIComponent(term)}`,
          { signal: abortController.signal },
        );
        if (abortController.signal.aborted || !searchValueRef.current.trim()) {
          return;
        }
        if (Array.isArray(response.data)) {
          const favorites = selectedSymbols();
          const filtered = response.data.filter(
            (stock: SearchSuggestion) =>
              !favorites.has(stock.ticker_symbol?.toLowerCase()),
          );
          setSearchSuggestions(filtered.slice(0, 8));
          setIsSearchFocused(true);
        } else {
          setSearchSuggestions([]);
        }
      } catch (error) {
        if (abortController.signal.aborted || axios.isCancel(error)) {
          return;
        }
        console.error('Error fetching search suggestions:', error);
        setSearchSuggestions([]);
      }
    };

    performSearch();
    return () => abortController.abort();
  }, [debouncedSearchValue, selectedSymbols]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setActiveSearchIndex(-1);
    if (value.trim()) {
      setIsSearchFocused(true);
    } else {
      setSearchSuggestions([]);
      setIsSearchFocused(false);
    }
  };

  const handleSelectStock = (stock: SearchSuggestion) => {
    onSelect(stock);
    setSearchValue('');
    setSearchSuggestions([]);
    setActiveSearchIndex(-1);
    setIsSearchFocused(false);
    searchInputRef.current?.focus();
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        if (searchSuggestions.length === 0) return;
        e.preventDefault();
        setActiveSearchIndex((prev) =>
          prev < searchSuggestions.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        if (searchSuggestions.length === 0) return;
        e.preventDefault();
        setActiveSearchIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeSearchIndex >= 0 && searchSuggestions.length > 0) {
          handleSelectStock(searchSuggestions[activeSearchIndex]);
        }
        break;
      case 'Escape':
        setSearchSuggestions([]);
        setActiveSearchIndex(-1);
        setIsSearchFocused(false);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsSearchFocused(false);
        setActiveSearchIndex(-1);
      }
    };

    if (isActive) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isActive]);

  useEffect(() => {
    if (activeSearchIndex < 0) return;
    const listbox = suggestionsListRef.current;
    if (!listbox) return;
    const option = listbox.children[activeSearchIndex] as HTMLElement | undefined;
    option?.scrollIntoView({ block: 'nearest' });
  }, [activeSearchIndex]);

  useEffect(() => {
    if (isActive) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      setSearchValue('');
      setSearchSuggestions([]);
      setActiveSearchIndex(-1);
      setIsSearchFocused(false);
    }
  }, [isActive]);

  return {
    searchValue,
    searchSuggestions,
    isSearchFocused,
    activeSearchIndex,
    searchInputRef,
    searchContainerRef,
    suggestionsListRef,
    handleSearchInputChange,
    handleSearchKeyDown,
    handleSelectStock,
    handleSearchFocus,
    setIsSearchFocused,
  };
};
